/**
 * Indian mobile numbers, as every typed number in nexseed is judged: ten digits starting 6-9. Web
 * inputs and API bodies share this one rule, so a number the screen accepts is never refused on
 * submit. Numbers that arrive from a feed (VNR Core, S-Capture) are stored as sent, not judged here.
 */
import { z } from "zod";

export const MOBILE_DIGITS = 10;
export const MOBILE_PATTERN = new RegExp(`^[6-9]\\d{${MOBILE_DIGITS - 1}}$`);
export const MOBILE_MESSAGE = `Enter a ${MOBILE_DIGITS}-digit mobile number starting 6–9`;

/**
 * An Indian mobile as it is stored: ten digits. People copy the number off a slip or a phone, so the
 * separators, a `+91` / `91` country code and a trunk `0` are all dropped. Idempotent.
 */
export function normalizeMobile(value: string | null | undefined): string {
  const compact = (value ?? "").trim().replace(/[\s-]/g, "");
  if (/^\+?91\d{10}$/.test(compact)) return compact.slice(-10);
  if (/^0\d{10}$/.test(compact)) return compact.slice(1);
  return compact;
}

/**
 * What a mobile box holds after each keystroke: digits only, never more than ten. A pasted
 * `+91 98765 43210` or `09876543210` arrives in one change event and is reduced to its ten digits
 * first; a mobile never starts with 0, so a typed trunk 0 is dropped too.
 */
export function limitMobileInput(value: string | null | undefined): string {
  return normalizeMobile(value)
    .replace(/^\+91/, "")
    .replace(/\D/g, "")
    .replace(/^0+/, "")
    .slice(0, MOBILE_DIGITS);
}

/** Why a typed mobile is refused, or undefined when it is fine. A blank optional number is fine. */
export function mobileError(
  value: string | null | undefined,
  { required = false }: { required?: boolean } = {}
): string | undefined {
  const v = normalizeMobile(value);
  if (!v) return required ? "Required" : undefined;
  return MOBILE_PATTERN.test(v) ? undefined : MOBILE_MESSAGE;
}

/** A typed mobile: normalised to its ten digits, then judged. */
export const mobileNumber = z
  .string()
  .transform(normalizeMobile)
  .pipe(z.string().regex(MOBILE_PATTERN, MOBILE_MESSAGE));
